import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend);

// Brand colors
const REVENUE_RGB = '226, 30, 38';   // secondary-500
const PROFIT_RGB  = '88, 145, 198';  // primary-400

function buildGradient(ctx, height, rgb) {
    const g = ctx.createLinearGradient(0, 0, 0, height);
    g.addColorStop(0,   `rgba(${rgb}, 0.30)`);
    g.addColorStop(0.6, `rgba(${rgb}, 0.06)`);
    g.addColorStop(1,   `rgba(${rgb}, 0)`);
    return g;
}

function AeraChart({ chartData = [], interval = '' }) {
    const chartRef = useRef(null);
    const theme = useSelector((s) => s.auth.theme);
    const isDark = theme === 'dark';

    const labels = useMemo(() => chartData.map((elem) => {
        const opts = interval === 'MONTH'
            ? { month: 'short', year: '2-digit' }
            : { month: 'short', day: 'numeric' };
        return new Date(elem.clickTime).toLocaleDateString('en-US', opts);
    }), [chartData, interval]);

    const revenueValues = useMemo(() => chartData.map((d) => d.revenue), [chartData]);
    const profitValues  = useMemo(() => chartData.map((d) => d.profit),  [chartData]);

    // Start with flat colours — updated to gradients once the canvas is mounted
    const [bgRevenue, setBgRevenue] = useState(`rgba(${REVENUE_RGB}, 0.20)`);
    const [bgProfit,  setBgProfit]  = useState(`rgba(${PROFIT_RGB},  0.15)`);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const h = chartArea.bottom - chartArea.top;
        setBgRevenue(buildGradient(ctx, h, REVENUE_RGB));
        setBgProfit( buildGradient(ctx, h, PROFIT_RGB));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData]);

    const data = useMemo(() => ({
        labels,
        datasets: [
            {
                fill: true,
                label: 'Revenue',
                data: revenueValues,
                borderColor: `rgb(${REVENUE_RGB})`,
                backgroundColor: bgRevenue,
                borderWidth: 2,
                pointRadius: 0,
                pointHitRadius: 32,
                tension: 0.4,
            },
            {
                fill: true,
                label: 'Profit',
                data: profitValues,
                borderColor: `rgb(${PROFIT_RGB})`,
                backgroundColor: bgProfit,
                borderWidth: 2,
                pointRadius: 0,
                pointHitRadius: 32,
                tension: 0.4,
            },
        ],
    }), [labels, revenueValues, profitValues, bgRevenue, bgProfit]);

    const textColor = isDark ? '#9ca3af' : '#6b7280';
    const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    borderRadius: 3,
                    useBorderRadius: true,
                    color: textColor,
                    font: { family: "'Mulish', sans-serif", size: 12, weight: '600' },
                    padding: 16,
                },
            },
            tooltip: {
                backgroundColor: '#0f1f30',
                titleColor: '#9ca3af',
                bodyColor: '#ffffff',
                borderColor: 'rgba(226,30,38,0.4)',
                borderWidth: 1,
                padding: 12,
                titleFont: { family: "'Mulish', sans-serif", size: 11 },
                bodyFont:  { family: "'Mulish', sans-serif", size: 13, weight: '700' },
                callbacks: {
                    label: (ctx) => ` ${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
            },
            title: { display: false },
        },
        scales: {
            x: {
                ticks: {
                    color: textColor,
                    padding: 6,
                    maxRotation: 0,
                    font: { family: "'Mulish', sans-serif", size: 11 },
                },
                grid:   { display: false },
                border: { display: false },
            },
            y: {
                beginAtZero: true,
                grace: '15%',
                ticks: {
                    color: textColor,
                    padding: 8,
                    font: { family: "'Mulish', sans-serif", size: 11 },
                    callback: (v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`,
                },
                grid:   { color: gridColor, lineWidth: 1 },
                border: { display: false },
            },
        },
    }), [textColor, gridColor]);

    return (
        <div className="w-full h-72">
            <Line ref={chartRef} options={options} data={data} />
        </div>
    );
}

export default AeraChart;
